// Lossless-timing, lossy-picture review proxy. Original remains on the editor's computer.
// Never specify a conversion frameRate, process timestamps or rebase XML to proxy fps.
// Guard every conversion; do not send unverified media to Supabase or AssemblyAI.
import {inspectMp4TimingFile} from './mediaInfo.js';
import {readVideoMetadata} from './importMedia.js';
import {PROXY_VIDEO_BITRATE,PROXY_AUDIO_BITRATE,validateProxyTiming} from './proxySafety.js';

function manualProxy(message){
 return new Error(message+' Use Premiere or Media Encoder to export H.264/AAC MP4 at 480p, with the ORIGINAL frame rate, then upload that proxy. Keep your original camera footage locally.');
}
export async function createReviewProxy(file,{metadata,sourceTiming,maxBytes,onProgress,onDiagnostic}={}){
 if(!file?.name.toLowerCase().endsWith('.mp4'))throw manualProxy('Automatic conversion currently supports MP4 sources only.');
 if(!sourceTiming || sourceTiming.frameStatus!=='detected-cfr' || !Number.isSafeInteger(sourceTiming.frameCount))
  throw manualProxy('The original footage has an unverified or variable frame rate.');
 if(!Number.isFinite(metadata?.duration)||!Number.isFinite(metadata?.height))
  throw manualProxy('The original video duration or dimensions cannot be verified.');
 // Dynamically import the heavy converter only if the editor opts in.
 const {
  Input,Output,ALL_FORMATS,BlobSource,Mp4OutputFormat,BufferTarget,
  Conversion,Quality,canEncodeVideo,canEncodeAudio
 }=await import('mediabunny');
 const input=new Input({formats:ALL_FORMATS,source:new BlobSource(file)});
 const videos=await input.getVideoTracks(),audios=await input.getAudioTracks();
 if(videos.length!==1 || audios.length!==1)throw manualProxy('Multiple or missing media tracks cannot safely be converted automatically.');
 const video=videos[0],audio=audios[0];
 const sourceCodec=await audio.getCodec(),channels=await audio.getNumberOfChannels(),sampleRate=await audio.getSampleRate();
 if(!sourceCodec || !Number.isInteger(channels)||channels<1||channels>2||!Number.isFinite(sampleRate)||sampleRate<8000)
  throw manualProxy('The original audio layout is not supported by the safe converter.');
 if(!await video.canDecode())throw manualProxy('This browser cannot decode the original camera video codec.');
 const width=await video.getDisplayWidth(),height=await video.getDisplayHeight();
 if(!width||!height)throw manualProxy('The video dimensions cannot be read.');
 // Fit inside 480p; do not upscale already smaller video.
 const targetHeight=Math.min(480,Math.round(height));
 const targetWidth=Math.max(2,Math.round((width/height*targetHeight)/2)*2);
 const targetEvenHeight=Math.max(2,Math.round(targetHeight/2)*2);
 const fps=sourceTiming.fpsNum/sourceTiming.fpsDen;
 const videoQuality=new Quality({bitrate:PROXY_VIDEO_BITRATE});
 if(!await canEncodeVideo('avc',{width:targetWidth,height:targetEvenHeight,frameRate:fps,quality:videoQuality}))
  throw manualProxy('H.264 encoding at the original frame rate is not available in this browser.');
 const copyAudio=sourceCodec==='aac';
 if(!copyAudio){
  if(!await audio.canDecode())throw manualProxy('This browser cannot decode the original audio codec.');
  if(!await canEncodeAudio('aac',{numberOfChannels:channels,sampleRate,quality:new Quality({bitrate:PROXY_AUDIO_BITRATE})}))
   throw manualProxy('High-quality AAC encoding is not available in this browser.');
 }
 const originalVideoStart=await video.getFirstTimestamp(),originalAudioStart=await audio.getFirstTimestamp();
 if(!Number.isFinite(originalVideoStart)||!Number.isFinite(originalAudioStart))
  throw manualProxy('The camera footage uses unsupported starting timestamps.');
 const output=new Output({format:new Mp4OutputFormat(),target:new BufferTarget()});
 const conversion=await Conversion.init({
  input,output,
  video:{height:targetEvenHeight,codec:'avc',quality:videoQuality},
  audio:copyAudio?{}:{codec:'aac',quality:new Quality({bitrate:PROXY_AUDIO_BITRATE})},
  copy:{shiftTolerance:0}
 });
 if(!conversion.isValid || conversion.discardedTracks.length)
  throw manualProxy('The converter cannot safely preserve all required audio/video tracks.');
 conversion.onProgress=value=>onProgress?.(Math.min(98,Math.max(0,Math.floor(value*98))));
 try{
  await conversion.execute();
 }catch(error){
  throw manualProxy('Local conversion failed ('+(error?.message||'unsupported media')+').');
 }
 const bytes=output.target.buffer;
 if(!bytes?.byteLength)throw manualProxy('The converter produced an empty video.');
 if(bytes.byteLength>maxBytes)
  throw new Error('The verified 480p proxy is '+Math.ceil(bytes.byteLength/1e6)+' MB, over the current '+Math.round(maxBytes/1e6)+' MB storage cap. Export a smaller proxy yourself, or use Takefold after upgrading Supabase Storage. The original file is untouched.');
 const name=file.name.replace(/\.mp4$/i,'')+'.takefold-proxy.mp4';
 const proxy=new File([bytes],name,{type:'video/mp4',lastModified:file.lastModified});
 const [proxyMeta,proxyTiming]=await Promise.all([readVideoMetadata(proxy),inspectMp4TimingFile(proxy)]);
 const reread=new Input({formats:ALL_FORMATS,source:new BlobSource(proxy)});
 const outputVideos=await reread.getVideoTracks(),outputAudios=await reread.getAudioTracks();
 if(outputVideos.length!==1||outputAudios.length!==1 || await outputAudios[0].getCodec()!=='aac')
  throw manualProxy('Verification found a missing or altered audio/video track.');
 if(await outputAudios[0].getNumberOfChannels()!==channels)
  throw manualProxy('Audio channel count changed.');
 const details={sourceTiming,proxyTiming,sourceDuration:metadata.duration,proxyDuration:proxyMeta.duration,
  sourceAudioStart:originalAudioStart,proxyAudioStart:await outputAudios[0].getFirstTimestamp(),
  sourceVideoStart:originalVideoStart,proxyVideoStart:await outputVideos[0].getFirstTimestamp()};
 onDiagnostic?.(details);
 validateProxyTiming(sourceTiming,proxyTiming,{
  duration:metadata.duration,proxyDuration:proxyMeta.duration,
  sourceAudioStart:originalAudioStart,proxyAudioStart:await outputAudios[0].getFirstTimestamp(),
  sourceVideoStart:originalVideoStart,proxyVideoStart:await outputVideos[0].getFirstTimestamp()
 });
 if(proxyMeta.height>480 && proxyMeta.width>480)throw manualProxy('Proxy resolution was not reduced to 480p.');
 onProgress?.(100);
 return {file:proxy,proxy:{
  filename:name,mediaBytes:proxy.size,width:proxyMeta.width,height:proxyMeta.height,
  sourceFilename:file.name,sourceBytes:file.size,
  sourceFpsNum:sourceTiming.fpsNum,sourceFpsDen:sourceTiming.fpsDen,
  frameCount:sourceTiming.frameCount,
  audio:'AAC '+(copyAudio?'copied from source':'256 kbps encoded'),
  verified:true
 }};
}
